import React, { useContext, useEffect, useState } from 'react';
import { useForm, SubmitHandler, Controller } from 'react-hook-form';
import { useQueryClient } from 'react-query';
import { useConfirm } from 'material-ui-confirm';
import { enqueueSnackbar } from 'notistack';
import parse from 'html-react-parser';
import * as Yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { Button, TextField, Box, InputLabel, Stack } from '@mui/material';

import { DraftManualCRQContext } from '../context';
import { translate as t } from 'hooks/useTranslations';
import useManageCRQ from 'views/App/Portal/Policy/DraftManuals/_data_sources/crq/useManageCRQ';
import useCRQ from 'views/App/Portal/Policy/DraftManuals/_data_sources/crq/useCRQ';
import { type ServerErrorResponse } from 'data_sources/constants';
import Modal from 'components/_layouts/Modal';
import errorHelper from 'helpers/errorHelper';
import Loading from 'views/App/Portal/Loading';
import { Delete } from '@mui/icons-material';
import { CRQChoice, CRQuestion } from 'views/App/Portal/Policy/_types/crq';
import ChoicesInput from '../ChoicesInput';
import draftManualHelper from '../../helpers/draftManualHelper';

export type EditQuestionProps = {
  deletable: boolean;
  questionId: string;
  setLastQuestionId: React.Dispatch<React.SetStateAction<string | null>>;
  setEditQuestionId: React.Dispatch<React.SetStateAction<string | null>>;
};

const EditQuestion: React.FC<EditQuestionProps> = ({
  deletable,
  questionId,
  setLastQuestionId,
  setEditQuestionId,
}: EditQuestionProps) => {
  const queryClient = useQueryClient();
  const confirm = useConfirm();
  const { manual } = useContext(DraftManualCRQContext);
  const [serverErrors, setServerErrors] = useState<ServerErrorResponse>();
  const [isSaving, setIsSaving] = useState(false);

  const { destroy: deleteQuestion, update: updateQuestion } = useManageCRQ();

  const crqQuery = useCRQ(manual.id, questionId);

  const crqQuestionSchema = Yup.object().shape({
    choices: Yup.array()
      .transform((value, originalValue) => {
        return originalValue.filter((x: CRQChoice) => x.value.length > 0);
      })
      .min(2, 'At least 2 choices are required for a question'),
    question: Yup.string()
      .trim()
      .transform((value: string, originalValue: string) => {
        return value.replace(/\s+/g, ' ');
      })
      .required(`${t('draftManuals.crq.properties.question')} is required`),
    hint: Yup.string()
      .trim()
      .transform((value: string, originalValue: string) => {
        return value.replace(/\s+/g, ' ');
      }),
  });

  const {
    control,
    register,
    handleSubmit,
    formState: { errors },
    reset,
    trigger,
    watch,
  } = useForm({
    resolver: yupResolver(crqQuestionSchema),
  });

  const handleSave: SubmitHandler<CRQuestion> = (values: CRQuestion) => {
    setIsSaving(true);
    const data: CRQuestion = {
      ...values,
      choices: (values.choices ?? [])
        .filter((x: CRQChoice) => x.value.length > 0)
        .map((x: CRQChoice) => ({
          value: x.value.trim(),
          content: x.content ?? [],
        })),
    };
    updateQuestion(
      manual.id,
      questionId,
      data,
      (data) => {
        reset();
        queryClient.invalidateQueries(['draft_manuals_crq']);
        if (data.id) setLastQuestionId(data.id);
        setEditQuestionId(null);

        setIsSaving(false);
      },
      (errors: ServerErrorResponse) => {
        setIsSaving(false);
        setServerErrors(errors);
      }
    );
  };

  const handleDelete = () => {
    setIsSaving(true);
    confirm({
      cancellationButtonProps: {
        variant: 'outlined',
        color: 'neutral',
      },
      confirmationButtonProps: {
        variant: 'contained',
        color: 'error',
      },
      confirmationText: t('common.delete'),
      description: t('draftManuals.crq.delete.body'),
    })
      .then(() => {
        deleteQuestion(
          manual.id,
          questionId,
          () => {
            reset();
            queryClient.invalidateQueries(['draft_manuals_crq']);
            setLastQuestionId(null);
            setEditQuestionId(null);
            enqueueSnackbar(parse(t(`draftManuals.crq.delete.success`)), {
              variant: 'success',
              autoHideDuration: 6000,
            });

            setIsSaving(false);
          },
          (errors: ServerErrorResponse) => {
            setIsSaving(false);
            setServerErrors(errors);
          }
        );
      })
      .catch(() => {
        // don't do it
      });
  };

  useEffect(() => {
    if (crqQuery.data?.question) {
      reset(crqQuery.data);
    }
  }, [crqQuery.data, questionId]);

  return (
    <>
      <Modal
        title={t('draftManuals.crq.edit.title')}
        onSubmit={handleSubmit(handleSave)}
        size='md'
        actions={
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              flex: 1,
            }}
          >
            <Box>
              {deletable && (
                <Button
                  className='icon-only'
                  color='error'
                  onClick={() => handleDelete()}
                >
                  <Delete />
                </Button>
              )}
            </Box>
            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
              <Button
                color='neutral'
                onClick={() => setEditQuestionId(null)}
              >
                {t('common.cancel')}
              </Button>
              <Button
                onClick={() => {
                  trigger();
                }}
                disabled={isSaving}
                component='button'
                type='submit'
                variant='contained'
              >
                {t('common.save')}
              </Button>
            </Box>
          </Box>
        }
        open
        onClose={() => setEditQuestionId(null)}
      >
        <>
          {crqQuery.isLoading ? (
            <Loading />
          ) : (
            <>
              {errorHelper.renderAllErrors(serverErrors, errors)}

              <TextField
                id='question'
                key='question'
                label={t('draftManuals.crq.properties.question')}
                variant='outlined'
                required
                fullWidth
                {...register('question')}
                error={!!errors.question}
                helperText={errors.question ? errors.question.message : undefined}
              />
              <TextField
                id='hint'
                key='hint'
                label={t('draftManuals.crq.properties.hint')}
                variant='outlined'
                fullWidth
                {...register('hint')}
                error={!!errors.hint}
                helperText={errors.hint ? errors.hint.message : undefined}
              />
              <Stack
                spacing={0}
                key='choices'
              >
                <InputLabel>{t('draftManuals.crq.properties.choices')}</InputLabel>
                <Controller
                  name='choices'
                  control={control}
                  render={({ field }) => (
                    <ChoicesInput
                      id='choices'
                      {...field}
                      manualContent={draftManualHelper.getManualContent(manual)}
                    />
                  )}
                />
              </Stack>
            </>
          )}
        </>
      </Modal>
    </>
  );
};

export default EditQuestion;
